import type { FeatureFlags, UserSettings } from './request.utils';

/**
 * Reading the 2026-10-02 prod-fix flags and settings on the web (PLAN.md section 0 "Flags", C3).
 *
 * Fail closed on purpose: the backend defaults the four kill switches to TRUE, but a page can hold settings
 * from an API that predates them (or the PROD flag set baked into SSR props), and "flag off means identical
 * to today" (UX-SPEC section 1) has to hold there too. So a fix is on only on an exact `true`.
 */
export type ProdFixFlag = 'uploadExpiryGate' | 'compressNoGainPolicy' | 'unsupportedInputDetection' | 'rerunDedupeOffer' | 'autoRetryTransient';

export function prodFixOn(flags: Partial<FeatureFlags> | null | undefined, flag: ProdFixFlag): boolean {
    return (flags as Record<string, unknown> | null | undefined)?.[flag] === true;
}

export interface UploadAgeThresholds {
    /** Past this many minutes since upload completion the upload is treated as gone (FIX-2 T1 trigger). */
    retentionMinutes: number;
    /** From this age on, Re-edit asks the server before opening (FIX-2 T2 trigger). */
    checkAfterMinutes: number;
}

/** The two FIX-2 thresholds, or null when the settings do not carry both as sane numbers (then: today's behaviour). */
export function uploadAgeThresholds(settings: Partial<UserSettings> | null | undefined): UploadAgeThresholds | null {
    const retention = settings?.uploadRetentionMinutes;
    const checkAfter = settings?.uploadCheckAfterMinutes;
    if (typeof retention !== 'number' || typeof checkAfter !== 'number') return null;
    if (!Number.isFinite(retention) || !Number.isFinite(checkAfter) || retention <= 0 || checkAfter < 0 || checkAfter > retention) return null;
    return { retentionMinutes: retention, checkAfterMinutes: checkAfter };
}
